// /Users/joshua.moore1153/Desktop/Sinclair Apps/spcore-v4/src/components/CcpAdminAnnouncements/inc/List.js
import React, { useState } from "react";
import GridToolbarNoExport from "../../../common/GridToolbarNoExport";
import AppDataGrid from "../../../common/AppDataGrid";
import {
  Dialog,
  DialogContent,
  IconButton,
  Snackbar,
  Alert, // Use Alert for better error display
  CircularProgress, // Show loading indicator
  Box, // For centering loading/error messages
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
// Removed useAnnouncementsHook import
import EditForm from "./EditForm";
import { formatDate } from "../../../../utils/formatDate";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

// Accept props from parent
const CrudTable = ({ data, loading, error, updateData, deleteData }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Dynamic snackbar message
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // Snackbar type (success, error)

  // Removed useAnnouncementsHook call

  const handleEditClick = (annId) => {
    const ann = data.find((item) => item.id === annId);
    if (ann) {
      setSelectedItem(ann);
      setEditDialogOpen(true);
    } else {
      console.error("Announcement not found for editing:", annId);
      // Optionally show an error message to the user
    }
  };

  const handleDeleteClick = (annId) => {
    const ann = data.find((item) => item.id === annId);
    if (ann) {
      setSelectedItem(ann);
      setDeleteDialogOpen(true);
    } else {
      console.error("Announcement not found for deletion:", annId);
      // Optionally show an error message to the user
    }
  };

  const handleEditFormClose = () => {
    setEditDialogOpen(false);
    setSelectedItem(null);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleUpdate = async (updatedItemData) => {
    // The EditForm passes the data *without* the id
    // The selectedItem holds the id
    if (!selectedItem || !selectedItem.id) {
      console.error("No selected item or item ID for update.");
      showSnackbar("Could not update announcement. Item not found.", "error");
      return;
    }
    try {
      // Pass id and the updated data separately, as expected by useFirestore's updateData
      await updateData(selectedItem.id, updatedItemData);
      showSnackbar("Announcement successfully updated.", "success");
      handleEditFormClose();
    } catch (err) {
      console.error("Error updating data:", err);
      showSnackbar(
        `Error updating announcement: ${err.message || "Please try again."}`,
        "error",
      );
    }
  };

  const handleDelete = async () => {
    if (!selectedItem || !selectedItem.id) {
      console.error("No selected item or item ID for delete.");
      showSnackbar("Could not delete announcement. Item not found.", "error");
      return;
    }
    try {
      await deleteData(selectedItem.id); // Use deleteData prop
      showSnackbar("Announcement successfully deleted.", "success");
      handleDeleteDialogClose();
    } catch (err) {
      console.error("Error deleting data:", err);
      showSnackbar(
        `Error deleting announcement: ${err.message || "Please try again."}`,
        "error",
      );
    }
  };

  const columns = [
    { field: "title", headerName: "Title", flex: 1, minWidth: 150 },
    { field: "color", headerName: "Color", flex: 0.5, minWidth: 80 },
    {
      field: "expireDate",
      headerName: "Expire Date",
      flex: 0.7,
      minWidth: 100,
      renderCell: (params) => formatDate(params.value) || "-",
    }, // Ensure it's treated as a date
    { field: "content", headerName: "Content", flex: 1.5, minWidth: 200 },
    {
      field: "url",
      headerName: "URL",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        if (params.value) {
          return (
            <a
              href={params.value}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "blue", textDecoration: "underline" }}
            >
              {params.value.length > 30
                ? `${params.value.substring(0, 30)}...`
                : params.value}
            </a>
          );
        }
        return "-";
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      disableExport: true, // Don't export actions column
      width: 120, // Fixed width for actions
      renderCell: (params) => (
        <Box>
          {" "}
          {/* Wrap icons for better layout control */}
          <IconButton
            onClick={() => handleEditClick(params.row.id)}
            color="primary"
            aria-label="edit announcement" // Accessibility
          >
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteClick(params.row.id)}
            color="error"
            aria-label="delete announcement" // Accessibility
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Display loading state
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Display error state
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Alert severity="error">Error loading announcements: {error}</Alert>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ height: 600, width: "100%" }}>
        {" "}
        {/* Set height for DataGrid */}
        <AppDataGrid
          rows={data} // Use data prop
          columns={columns}
          // loading={loading} // Loading state handled above
          slots={{ toolbar: GridToolbarNoExport }}
          // Removed pagination props, DataGridPro handles pagination state internally by default
          // pageSize={10} // You can set initial page size if needed via initialState
          // rowsPerPageOptions={[10, 25, 50]} // Configure options via initialState if needed
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10, 25, 50]} // Use pageSizeOptions in v6+
          disableRowSelectionOnClick // Changed from disableSelectionOnClick
          autoHeight={false} // Ensure grid takes up defined height
          // Consider adding density='compact' or 'standard'
        />
      </Box>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditFormClose}
        maxWidth="sm"
        fullWidth
      >
        <ModalDialogTitle onClose={handleEditFormClose}>
          Edit Announcement
        </ModalDialogTitle>
        <DialogContent>
          {/* Ensure selectedItem exists before rendering EditForm */}
          {selectedItem && (
            <EditForm
              // Pass only the data fields, not the id, to EditForm's initialData
              initialData={{
                title: selectedItem.title,
                color: selectedItem.color,
                expireDate: selectedItem.expireDate,
                content: selectedItem.content,
                url: selectedItem.url, // Include URL in edit form data
                linkText: selectedItem.linkText, // Include link text in edit form data
              }}
              onUpdate={handleUpdate}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete the announcement titled "${selectedItem?.title}"? This action cannot be undone.`}
      />

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        {/* Use Alert inside Snackbar for severity indication */}
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CrudTable;
